import React, { useState, useEffect, useRef, useCallback } from "react";
import * as XLSX from 'xlsx';
import { FaFileDownload, FaFileUpload } from 'react-icons/fa';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  FormControl,
  Checkbox,
  FormGroup,
  Divider,
  Alert,
  Chip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const mobilePrefixesTL = /^(77|78|73|74|75|76|70|71|72)/;

const requiredFieldsList = [
  "fullName", "gender", "ageCategory", "hasDisability",
  "level", "employmentType", "mobileNumber", "email", "preferredPlatform"
];

const convertCamelToSnakeCase = (obj) => {
  const newObj = {};
  for (const camel in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, camel)) {
      const snake = camel.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      newObj[snake] = obj[camel];
    }
  }
  return newObj;
};

const convertSnakeToCamelCase = (obj) => {
  const newObj = {};
  for (const snake in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, snake)) {
      const camel = snake.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
      newObj[camel] = obj[snake];
    }
  }
  return newObj;
};

// ─── Reusable multi-select dropdown ──────────────────────────────────────────
const MultiSelectDropdown = ({ label, options, selected, onToggle, disabled, dropdownRef, showDropdown, setShowDropdown, onBlur, getLabel, getValue }) => (
  <Box ref={dropdownRef} sx={{ position: 'relative' }}>
    <FormLabel sx={{ fontSize: '15px', fontWeight: 600, color: '#374151', mb: 0.5, display: 'block' }}>{label}</FormLabel>
    <Button
      variant="outlined"
      fullWidth
      disabled={disabled}
      onClick={() => setShowDropdown(!showDropdown)}
      onBlur={onBlur}
      endIcon={<KeyboardArrowDownIcon sx={{ transition: 'transform 0.2s', transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }} />}
      sx={{
        justifyContent: 'space-between',
        textAlign: 'left',
        textTransform: 'none',
        color: selected.length > 0 ? '#1e293b' : '#94a3b8',
        borderColor: '#d1d5db',
        background: disabled ? '#f8fafc' : '#fff',
        fontSize: '15px',
        fontWeight: 400,
        px: 1.5,
        py: '7px',
        '&:hover': { borderColor: '#2563eb', background: '#fff' },
        '&.Mui-disabled': { borderColor: '#e2e8f0', color: '#94a3b8' },
      }}
    >
      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
        {selected.length > 0
          ? selected.map(v => getLabel(v)).join(', ')
          : (disabled ? 'Select parent first' : `Select ${label.replace(' *', '')}`)}
      </Box>
    </Button>
    {showDropdown && (
      <Paper elevation={4} sx={{ position: 'absolute', width: '100%', maxHeight: 200, overflowY: 'auto', zIndex: 1300, mt: 0.5, border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        {options.length === 0 ? (
          <Typography sx={{ p: 1.5, fontSize: '15px', color: '#94a3b8' }}>No options available</Typography>
        ) : (
          options.map((opt) => (
            <Box
              key={getValue(opt)}
              sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.5, cursor: 'pointer', '&:hover': { background: '#f1f5f9' } }}
              onClick={() => onToggle(getValue(opt), !selected.includes(getValue(opt)))}
            >
              <Checkbox
                size="small"
                checked={selected.includes(getValue(opt))}
                onChange={() => {}}
                sx={{ p: 0.5, color: '#94a3b8', '&.Mui-checked': { color: '#2563eb' } }}
              />
              <Typography sx={{ fontSize: '15px', color: '#1e293b', ml: 1 }}>{getLabel(opt)}</Typography>
            </Box>
          ))
        )}
      </Paper>
    )}
  </Box>
);

const SectionLabel = ({ text }) => (
  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 2 }}>
    {text}
  </Typography>
);

const FieldLabel = ({ text, required }) => (
  <FormLabel sx={{ fontSize: '14px', fontWeight: 600, color: '#374151', mb: 0.5, display: 'block' }}>
    {text}{required && <Box component="span" sx={{ color: '#ef4444', ml: 0.5 }}>*</Box>}
  </FormLabel>
);

// ─── Component ────────────────────────────────────────────────────────────────
const OfficialsinfoForm = () => {
  const initialFormData = {
    fullName: "", gender: "", ageCategory: "", hasDisability: "", disabilityTypes: [],
    department: "", position: "", level: "", employmentType: "", modules: [],
    mobileNumber: "", whatsappNumber: "", email: "", retirementDate: "",
    preferredPlatform: [], municipality: [], postAdmin: [], suco: [],
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [showModulesDropdown, setShowModulesDropdown] = useState(false);
  const [isWhatsappSameAsMobile, setIsWhatsappSameAsMobile] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const fileInputRef = useRef(null);

  const modulesDropdownRef = useRef(null);
  const municipalityDropdownRef = useRef(null);
  const postAdminDropdownRef = useRef(null);
  const sucoDropdownRef = useRef(null);

  const [showMunicipalityDropdown, setShowMunicipalityDropdown] = useState(false);
  const [showPostAdminDropdown, setShowPostAdminDropdown] = useState(false);
  const [showSucoDropdown, setShowSucoDropdown] = useState(false);

  const [municipalityNameToCodeMap, setMunicipalityNameToCodeMap] = useState({});
  const [postAdminNameToCodeMap, setPostAdminNameToCodeMap] = useState({});
  const [sucoNameToCodeMap, setSucoNameToCodeMap] = useState({});

  const [, setAllMunicipalities] = useState([]);
  const [allPostAdmins, setAllPostAdmins] = useState([]);
  const [allSucos, setAllSucos] = useState([]);

  const [displayMunicipalities, setDisplayMunicipalities] = useState([]);
  const [displayPostAdmins, setDisplayPostAdmins] = useState([]);
  const [displaySucos, setDisplaySucos] = useState([]);

  const availableDepartments = ["DNMG", "Civil Protection", "AMN", "DRM", "HEALTH", "POLICE", "UN AGENCIES", "INGOs", "AAWG", "Local NGOs"];
  const availablePostions = ["Officer", "Assistant", "Manager", "Director", "Specialist", "Analyst"];
  const availableEmploymentTypes = ["Permanent", "Contract", "Intern"];
  const availableModules = ["Cyclone", "Landslide", "Earthquake", "Flood Risk", "Fire Risk", "Lightning", "Road Safety Condition"];
  const ageCategories = ["Below 18", "Between 19-49", "More 50"];
  const disabilityOptions = ["Seeing", "Listening", "Walking", "Speaking", "Others"];
  const availablePlatforms = ["Email", "WhatsApp"];

  const validateForm = useCallback((data) => {
    let newErrors = {};
    let currentIsValid = true;
    requiredFieldsList.forEach(field => {
      if (['municipality', 'postAdmin', 'suco', 'preferredPlatform'].includes(field)) {
        if (data[field].length === 0) {
          if (field === "municipality" && (data.level === "Municipality" || data.level === "Post Admin" || data.level === "Suco")) { newErrors[field] = "At least one Municipality is required."; currentIsValid = false; }
          if (field === "postAdmin" && (data.level === "Post Admin" || data.level === "Suco")) { newErrors[field] = "At least one Post Admin is required."; currentIsValid = false; }
          if (field === "suco" && data.level === "Suco") { newErrors[field] = "At least one Suco is required."; currentIsValid = false; }
          if (field === "preferredPlatform") { newErrors[field] = "At least one platform is required."; currentIsValid = false; }
        }
      } else if (!data[field] || (typeof data[field] === 'string' && data[field].trim() === "") || (Array.isArray(data[field]) && data[field].length === 0)) {
        newErrors[field] = "This field is required.";
        currentIsValid = false;
      }
    });
    if (data.hasDisability === 'Yes' && data.disabilityTypes.length === 0) { newErrors.disabilityTypes = "Select at least one disability type."; currentIsValid = false; }
    const nameRegex = /^[A-Za-z\s.'-]*$/;
    if (data.fullName && !nameRegex.test(data.fullName)) { newErrors.fullName = "Only letters, spaces, apostrophes, and hyphens allowed."; currentIsValid = false; }
    const emailRegex = /^[^\s@]+@([^\s@]+\.)+[^\s@]{2,}$/;
    if (data.email && data.email.trim() !== "" && !emailRegex.test(data.email)) { newErrors.email = "Invalid email format (e.g., name@domain.com)."; currentIsValid = false; }
    if (data.mobileNumber && data.mobileNumber.trim() !== "") {
      const d = data.mobileNumber.replace(/[\s-]/g, '');
      if (!mobilePrefixesTL.test(d) || d.length !== 8) { newErrors.mobileNumber = "Must be 8 digits starting with 77/78/73–76/70–72."; currentIsValid = false; }
    }
    if (data.whatsappNumber && data.whatsappNumber.trim() !== "" && !isWhatsappSameAsMobile) {
      const d = data.whatsappNumber.replace(/[\s-]/g, '');
      if (!mobilePrefixesTL.test(d) || d.length !== 8) { newErrors.whatsappNumber = "Must be 8 digits starting with 77/78/73–76/70–72."; currentIsValid = false; }
    }
    if (data.retirementDate && data.retirementDate.trim() !== "" && !/^\d{4}-\d{2}-\d{2}$/.test(data.retirementDate)) { newErrors.retirementDate = "Use YYYY-MM-DD format."; currentIsValid = false; }
    setErrors(newErrors);
    setIsFormValid(currentIsValid && Object.keys(newErrors).length === 0);
    return currentIsValid && Object.keys(newErrors).length === 0;
  }, [isWhatsappSameAsMobile]);

  const handleBlur = useCallback((field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateForm(formData);
  }, [validateForm, formData]);

  useEffect(() => {
    const fetchGeographicData = async () => {
      try {
        const [muniRes, postAdminRes, sucoRes] = await Promise.all([
          axios.post(`${backendUrl}Municipalities/municipalities_list/`),
          axios.post(`${backendUrl}Municipalities/sub_municipalities_list/`),
          axios.post(`${backendUrl}Municipalities/suco_list/`)
        ]);
        const municipalities = muniRes.data.map(m => ({ code: m.adm1_code, name: m.adm1 }));
        setAllMunicipalities(municipalities);
        setDisplayMunicipalities(municipalities);
        const muniNameMap = {};
        municipalities.forEach(muni => muniNameMap[muni.name] = muni.code);
        setMunicipalityNameToCodeMap(muniNameMap);

        const postAdmins = postAdminRes.data.map(pa => ({ code: pa.adm2_code, name: pa.adm2, parent_adm1_code: pa.adm1_code }));
        setAllPostAdmins(postAdmins);
        const postAdminNameMap = {};
        postAdmins.forEach(pa => postAdminNameMap[pa.name] = pa.code);
        setPostAdminNameToCodeMap(postAdminNameMap);

        const sucos = sucoRes.data.map(s => ({ code: s.adm3_code, name: s.adm3, parent_adm2_code: s.adm2_code }));
        setAllSucos(sucos);
        const sucoNameMap = {};
        sucos.forEach(s => sucoNameMap[s.name] = s.code);
        setSucoNameToCodeMap(sucoNameMap);
      } catch (error) {
        console.error('Error fetching geographic data:', error);
      }
    };
    fetchGeographicData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modulesDropdownRef.current && !modulesDropdownRef.current.contains(event.target)) setShowModulesDropdown(false);
      if (municipalityDropdownRef.current && !municipalityDropdownRef.current.contains(event.target)) { setShowMunicipalityDropdown(false); handleBlur("municipality"); }
      if (postAdminDropdownRef.current && !postAdminDropdownRef.current.contains(event.target)) { setShowPostAdminDropdown(false); handleBlur("postAdmin"); }
      if (sucoDropdownRef.current && !sucoDropdownRef.current.contains(event.target)) { setShowSucoDropdown(false); handleBlur("suco"); }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleBlur]);

  const updateFormState = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      const updatedTouched = { ...touched, [field]: true };
      setTouched(updatedTouched);
      if (field === "level") {
        updated.municipality = []; updated.postAdmin = []; updated.suco = [];
        setShowMunicipalityDropdown(false); setShowPostAdminDropdown(false); setShowSucoDropdown(false);
        setDisplayPostAdmins([]); setDisplaySucos([]);
      } else if (field === "municipality" && Array.isArray(value)) {
        const newPAs = allPostAdmins.filter(pa => value.includes(pa.parent_adm1_code));
        updated.postAdmin = updated.postAdmin.filter(c => newPAs.some(pa => pa.code === c));
        updated.suco = [];
        setDisplayPostAdmins(newPAs); setDisplaySucos([]);
      } else if (field === "postAdmin" && Array.isArray(value)) {
        const newSucos = allSucos.filter(s => value.includes(s.parent_adm2_code));
        updated.suco = updated.suco.filter(c => newSucos.some(s => s.code === c));
        setDisplaySucos(newSucos);
      }
      validateForm(updated);
      return updated;
    });
  };

  const handleMultiSelectChange = (field, value, isChecked) => {
    const current = formData[field];
    updateFormState(field, isChecked ? [...current, value] : current.filter(i => i !== value));
  };

  const handlePhoneInputChange = (field, e) => {
    updateFormState(field, e.target.value.replace(/[^0-9\s-]/g, ''));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const allTouched = Object.keys(formData).reduce((acc, k) => { acc[k] = true; return acc; }, {});
    setTouched(allTouched);
    if (!validateForm(formData)) { setSubmitError('Please fill in all required fields correctly.'); return; }
    try {
      const dataToSend = { ...formData, preferredPlatform: formData.preferredPlatform.join(',') };
      const snakeCaseData = convertCamelToSnakeCase(dataToSend);
      await axios.post(`${backendUrl}dissemination/officials/`, snakeCaseData);
      alert('Official details submitted successfully!');
      setFormData(initialFormData);
      setErrors({}); setTouched({}); setIsWhatsappSameAsMobile(false); setSubmitError('');
    } catch (error) {
      console.error('Submission error:', error.response ? error.response.data : error.message);
      if (error.response && error.response.data) {
        const camelErrors = convertSnakeToCamelCase(error.response.data);
        setErrors(camelErrors); setTouched(allTouched);
        setSubmitError('Submission failed. Please review the highlighted fields.');
      } else {
        setSubmitError('An error occurred. Please try again.');
      }
    }
  };

  const handleDownloadSample = () => {
    const sampleData = [
      ["fullName", "gender", "ageCategory", "hasDisability", "disabilityTypes", "department", "position", "level", "employmentType", "municipality", "postAdmin", "suco", "modules", "mobileNumber", "whatsappNumber", "email", "retirementDate", "preferredPlatform"],
      ["John Doe", "Male", "Between 19-49", "No", "", "DNMG", "Officer", "Municipality", "Permanent", "Dili,Baucau", "Dom Aleixo,Baucau Vila", "Comoro,Buibau", "Cyclone,Flood Risk", "77123456", "77654321", "john.doe@example.org", "2040-12-31", "Email,WhatsApp"],
      ["Jane Smith", "Female", "More 50", "Yes", "Seeing,Walking", "HEALTH", "Assistant", "Suco", "Contract", "Dili", "Dom Aleixo", "Comoro", "Earthquake", "78987654", "", "jane.smith@example.org", "2025-12-31", "WhatsApp"]
    ];
    const ws = XLSX.utils.aoa_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Officials_Sample");
    XLSX.writeFile(wb, "Officials_Sample_Form.xlsx");
  };

  const excelDateToJSDate = (excelDate) => {
    if (typeof excelDate === 'number') return new Date((excelDate - 25569) * 86400 * 1000).toISOString().split('T')[0];
    return excelDate;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      if (json.length > 1) {
        const headers = json[0].map(h => h.replace(/\s+/g, ''));
        const headerMap = { fullName: "full_name", ageCategory: "age_category", hasDisability: "has_disability", disabilityTypes: "disability_types", employmentType: "employment_type", mobileNumber: "mobile_number", whatsappNumber: "whatsapp_number", retirementDate: "retirement_date", preferredPlatform: "preferred_platform", postAdmin: "post_admin", department: "department", position: "position", level: "level", municipality: "municipality", suco: "suco", modules: "modules", gender: "gender", email: "email" };
        const bulkRecords = json.slice(1).map(rowData => {
          const uploadedData = {};
          headers.forEach((h, i) => {
            const bField = headerMap[h] || h;
            let value = rowData[i];
            if (h === "municipality" && typeof value === 'string') value = value.split(',').map(n => n.trim()).map(n => municipalityNameToCodeMap[n]).filter(Boolean);
            if (h === "postAdmin" && typeof value === 'string') value = value.split(',').map(n => n.trim()).map(n => postAdminNameToCodeMap[n]).filter(Boolean);
            if (h === "suco" && typeof value === 'string') value = value.split(',').map(n => n.trim()).map(n => sucoNameToCodeMap[n]).filter(Boolean);
            if (['modules', 'disabilityTypes', 'preferredPlatform'].includes(h)) value = typeof value === 'string' ? value.split(',').map(s => s.trim()).filter(Boolean) : [];
            uploadedData[bField] = value;
          });
          if (uploadedData.retirement_date) uploadedData.retirement_date = excelDateToJSDate(uploadedData.retirement_date);
          uploadedData.age_category = uploadedData.age_category || "";
          uploadedData.has_disability = uploadedData.has_disability || "";
          uploadedData.employment_type = uploadedData.employment_type || "";
          uploadedData.municipality = uploadedData.municipality || [];
          uploadedData.post_admin = uploadedData.post_admin || [];
          uploadedData.suco = uploadedData.suco || [];
          uploadedData.modules = uploadedData.modules || [];
          uploadedData.disability_types = uploadedData.disability_types || [];
          uploadedData.preferred_platform = uploadedData.preferred_platform || [];
          const hasMissing = requiredFieldsList.some(ff => { const bf = headerMap[ff] || ff; const v = uploadedData[bf]; return !v || (Array.isArray(v) && v.length === 0); });
          if (hasMissing) { console.warn("Skipping record due to missing fields:", uploadedData); return null; }
          return uploadedData;
        }).filter(Boolean);
        sendBulkDataToBackend(bulkRecords);
      } else {
        alert('No data found in the Excel sheet or invalid format.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const sendBulkDataToBackend = async (records) => {
    if (records.length === 0) { alert("No valid records found."); return; }
    try {
      const recordsToSend = records.map(r => convertCamelToSnakeCase({ ...r, preferred_platform: Array.isArray(r.preferred_platform) ? r.preferred_platform.join(',') : r.preferred_platform }));
      await axios.post(`${backendUrl}dissemination/officials/bulk-upload/`, recordsToSend);
      alert('Data from Excel submitted successfully!');
    } catch (error) {
      console.error('Bulk upload error:', error.response ? error.response.data : error.message);
      alert('An error occurred during bulk upload.');
    }
  };

  const triggerFileUpload = () => fileInputRef.current.click();

  // ─── Shared styles ─────────────────────────────────────────────────────────
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '15px',
      '& fieldset': { borderColor: '#d1d5db' },
      '&:hover fieldset': { borderColor: '#2563eb' },
      '&.Mui-focused fieldset': { borderColor: '#2563eb', borderWidth: 1.5 },
    },
    '& .MuiInputLabel-root': { fontSize: '15px', '&.Mui-focused': { color: '#2563eb' } },
    '& .MuiFormHelperText-root': { fontSize: '12px', mt: 0.25 },
  };

  const radioSx = { color: '#94a3b8', '&.Mui-checked': { color: '#2563eb' } };
  const checkSx = { p: 0.5, color: '#94a3b8', '&.Mui-checked': { color: '#2563eb' } };

  const muniGetLabel = (opt) => typeof opt === 'object' ? opt.name : displayMunicipalities.find(m => m.code === opt)?.name || opt;
  const paGetLabel = (opt) => typeof opt === 'object' ? opt.name : displayPostAdmins.find(p => p.code === opt)?.name || opt;
  const sucoGetLabel = (opt) => typeof opt === 'object' ? opt.name : displaySucos.find(s => s.code === opt)?.name || opt;
  const getCode = (opt) => typeof opt === 'object' ? opt.code : opt;

  return (
    <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>Officials Info Form</Typography>
          {/* <Typography sx={{ fontSize: '12px', color: '#64748b', mt: 0.25 }}>Add dissemination staff information</Typography> */}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FaFileDownload size={12} />}
            onClick={handleDownloadSample}
            sx={{ fontSize: '13px', textTransform: 'none', borderColor: '#2563eb', color: '#2563eb', borderRadius: '7px', '&:hover': { background: '#eff6ff', borderColor: '#2563eb' } }}
          >
            Download Sample
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FaFileUpload size={12} />}
            onClick={triggerFileUpload}
            sx={{ fontSize: '13px', textTransform: 'none', borderColor: '#2563eb', color: '#2563eb', borderRadius: '7px', '&:hover': { background: '#eff6ff', borderColor: '#2563eb' } }}
          >
            Upload Data
          </Button>
          <input type="file" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} accept=".xlsx, .xls" />
        </Box>
      </Box>

      {/* ── Form Body ── */}
      <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>

        {submitError && <Alert severity="error" sx={{ fontSize: '14px', borderRadius: '8px' }}>{submitError}</Alert>}

        {/* ── Personal Information ── */}
        <Box>
          <SectionLabel text="Personal Information" />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
            <TextField
              label="Full Name *"
              value={formData.fullName}
              onChange={(e) => updateFormState("fullName", e.target.value)}
              onBlur={() => handleBlur("fullName")}
              size="small" fullWidth sx={inputSx}
              error={Boolean(touched.fullName && errors.fullName)}
              helperText={touched.fullName && errors.fullName}
            />
            <TextField
              label="Gender *"
              value={formData.gender}
              onChange={(e) => updateFormState("gender", e.target.value)}
              onBlur={() => handleBlur("gender")}
              select size="small" fullWidth sx={inputSx}
              error={Boolean(touched.gender && errors.gender)}
              helperText={touched.gender && errors.gender}
            >
              <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select Gender</em></MenuItem>
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Prefer-not-to-say">Prefer not to say</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </TextField>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mt: 2.5 }}>
            <FormControl>
              <FieldLabel text="Age Category (years old)" required />
              <RadioGroup row value={formData.ageCategory} onChange={(e) => updateFormState("ageCategory", e.target.value)}>
                {ageCategories.map((cat) => (
                  <FormControlLabel key={cat} value={cat}
                    control={<Radio size="small" sx={radioSx} />}
                    label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>{cat}</Typography>}
                    sx={{ mr: 1.5 }}
                  />
                ))}
              </RadioGroup>
              {touched.ageCategory && errors.ageCategory && <Typography sx={{ fontSize: '12px', color: '#ef4444', mt: 0.25 }}>{errors.ageCategory}</Typography>}
            </FormControl>

            <FormControl>
              <FieldLabel text="Person with Disabilities?" required />
              <RadioGroup row value={formData.hasDisability} onChange={(e) => updateFormState("hasDisability", e.target.value)}>
                <FormControlLabel value="No" control={<Radio size="small" sx={radioSx} />} label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>No</Typography>} sx={{ mr: 2 }} />
                <FormControlLabel value="Yes" control={<Radio size="small" sx={radioSx} />} label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>Yes</Typography>} />
              </RadioGroup>
              {formData.hasDisability === 'Yes' && (
                <FormGroup row sx={{ mt: 0.5 }}>
                  {disabilityOptions.map((type) => (
                    <FormControlLabel key={type}
                      control={<Checkbox size="small" checked={formData.disabilityTypes.includes(type)} onChange={(e) => handleMultiSelectChange("disabilityTypes", type, e.target.checked)} sx={checkSx} />}
                      label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>{type}</Typography>}
                      sx={{ mr: 1 }}
                    />
                  ))}
                </FormGroup>
              )}
              {touched.hasDisability && errors.hasDisability && <Typography sx={{ fontSize: '11px', color: '#ef4444', mt: 0.25 }}>{errors.hasDisability}</Typography>}
              {touched.disabilityTypes && errors.disabilityTypes && <Typography sx={{ fontSize: '11px', color: '#ef4444', mt: 0.25 }}>{errors.disabilityTypes}</Typography>}
            </FormControl>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#e2e8f0' }} />

        {/* ── Work Information ── */}
        <Box>
          <SectionLabel text="Work Information" />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
            <Box>
              <TextField
                label="Department"
                value={formData.department}
                onChange={(e) => updateFormState("department", e.target.value)}
                onBlur={() => handleBlur("department")}
                size="small" fullWidth sx={inputSx}
                inputProps={{ list: "departmentOptions" }}
                placeholder="Select or type"
              />
              <datalist id="departmentOptions">
                {availableDepartments.map((d, i) => <option key={i} value={d} />)}
              </datalist>
            </Box>
            <Box>
              <TextField
                label="Position"
                value={formData.position}
                onChange={(e) => updateFormState("position", e.target.value)}
                onBlur={() => handleBlur("position")}
                size="small" fullWidth sx={inputSx}
                inputProps={{ list: "positionOptions" }}
                placeholder="Select or type"
              />
              <datalist id="positionOptions">
                {availablePostions.map((p, i) => <option key={i} value={p} />)}
              </datalist>
            </Box>
            <TextField
              label="Employment Type *"
              value={formData.employmentType}
              onChange={(e) => updateFormState("employmentType", e.target.value)}
              onBlur={() => handleBlur("employmentType")}
              select size="small" fullWidth sx={inputSx}
              error={Boolean(touched.employmentType && errors.employmentType)}
              helperText={touched.employmentType && errors.employmentType}
            >
              <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select Employment Type</em></MenuItem>
              {availableEmploymentTypes.map((t, i) => <MenuItem key={i} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField
              label="Level *"
              value={formData.level}
              onChange={(e) => updateFormState("level", e.target.value)}
              onBlur={() => handleBlur("level")}
              select size="small" fullWidth sx={inputSx}
              error={Boolean(touched.level && errors.level)}
              helperText={touched.level && errors.level}
            >
              <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select Level</em></MenuItem>
              <MenuItem value="Country">Country</MenuItem>
              <MenuItem value="Municipality">Municipality</MenuItem>
              <MenuItem value="Post Admin">Post Admin</MenuItem>
              <MenuItem value="Suco">Suco</MenuItem>
            </TextField>
          </Box>
        </Box>

        {/* ── Geographic Scope ── */}
        {(formData.level === "Municipality" || formData.level === "Post Admin" || formData.level === "Suco") && (
          <>
            <Divider sx={{ borderColor: '#e2e8f0' }} />
            <Box>
              <SectionLabel text="Geographic Scope" />
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: formData.level === "Suco" ? '1fr 1fr 1fr' : formData.level === "Post Admin" ? '1fr 1fr' : '1fr',
                gap: 2.5
              }}>
                <MultiSelectDropdown
                  label="Municipality *"
                  options={displayMunicipalities}
                  selected={formData.municipality}
                  onToggle={(val, checked) => handleMultiSelectChange("municipality", val, checked)}
                  disabled={false}
                  dropdownRef={municipalityDropdownRef}
                  showDropdown={showMunicipalityDropdown}
                  setShowDropdown={setShowMunicipalityDropdown}
                  onBlur={() => handleBlur("municipality")}
                  getLabel={muniGetLabel}
                  getValue={getCode}
                />
                {(formData.level === "Post Admin" || formData.level === "Suco") && (
                  <MultiSelectDropdown
                    label="Post Admin *"
                    options={displayPostAdmins}
                    selected={formData.postAdmin}
                    onToggle={(val, checked) => handleMultiSelectChange("postAdmin", val, checked)}
                    disabled={formData.municipality.length === 0}
                    dropdownRef={postAdminDropdownRef}
                    showDropdown={showPostAdminDropdown}
                    setShowDropdown={setShowPostAdminDropdown}
                    onBlur={() => handleBlur("postAdmin")}
                    getLabel={paGetLabel}
                    getValue={getCode}
                  />
                )}
                {formData.level === "Suco" && (
                  <MultiSelectDropdown
                    label="Suco *"
                    options={displaySucos}
                    selected={formData.suco}
                    onToggle={(val, checked) => handleMultiSelectChange("suco", val, checked)}
                    disabled={formData.postAdmin.length === 0}
                    dropdownRef={sucoDropdownRef}
                    showDropdown={showSucoDropdown}
                    setShowDropdown={setShowSucoDropdown}
                    onBlur={() => handleBlur("suco")}
                    getLabel={sucoGetLabel}
                    getValue={getCode}
                  />
                )}
              </Box>
            </Box>
          </>
        )}

        <Divider sx={{ borderColor: '#e2e8f0' }} />

        {/* ── Contact & Alert Preferences ── */}
        <Box>
          <SectionLabel text="Contact & Alert Preferences" />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
            <Box>
              <TextField
                label="Mobile Number *"
                value={formData.mobileNumber}
                onChange={(e) => handlePhoneInputChange("mobileNumber", e)}
                onBlur={() => handleBlur("mobileNumber")}
                size="small" fullWidth sx={inputSx}
                placeholder="e.g., 77XXXXXXX"
                error={Boolean(touched.mobileNumber && errors.mobileNumber)}
                helperText={touched.mobileNumber && errors.mobileNumber}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={isWhatsappSameAsMobile}
                    onChange={(e) => { setIsWhatsappSameAsMobile(e.target.checked); updateFormState("whatsappNumber", e.target.checked ? formData.mobileNumber : ""); }}
                    sx={checkSx}
                  />
                }
                label={<Typography sx={{ fontSize: '12px', color: '#64748b' }}>WhatsApp same as Mobile</Typography>}
                sx={{ mt: 0.5, ml: 0 }}
              />
            </Box>
            <TextField
              label="WhatsApp Number"
              value={formData.whatsappNumber}
              onChange={(e) => handlePhoneInputChange("whatsappNumber", e)}
              onBlur={() => handleBlur("whatsappNumber")}
              size="small" fullWidth sx={inputSx}
              placeholder="e.g., 77XXXXXXX"
              disabled={isWhatsappSameAsMobile}
              error={Boolean(touched.whatsappNumber && errors.whatsappNumber)}
              helperText={touched.whatsappNumber && errors.whatsappNumber}
            />
            <TextField
              label="Email *"
              type="email"
              value={formData.email}
              onChange={(e) => updateFormState("email", e.target.value)}
              onBlur={() => handleBlur("email")}
              size="small" fullWidth sx={inputSx}
              placeholder="name@domain.com"
              error={Boolean(touched.email && errors.email)}
              helperText={touched.email && errors.email}
            />
            <TextField
              label="Retirement Date"
              type="date"
              value={formData.retirementDate}
              onChange={(e) => updateFormState("retirementDate", e.target.value)}
              onBlur={() => handleBlur("retirementDate")}
              size="small" fullWidth sx={inputSx}
              InputLabelProps={{ shrink: true }}
              error={Boolean(touched.retirementDate && errors.retirementDate)}
              helperText={touched.retirementDate && errors.retirementDate}
            />
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <FormControl>
              <FieldLabel text="Preferred Alert Platform" required />
              <FormGroup row>
                {availablePlatforms.map((platform) => (
                  <FormControlLabel key={platform}
                    control={<Checkbox size="small" checked={formData.preferredPlatform.includes(platform)} onChange={(e) => handleMultiSelectChange("preferredPlatform", platform, e.target.checked)} sx={checkSx} />}
                    label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>{platform}</Typography>}
                    sx={{ mr: 3 }}
                  />
                ))}
              </FormGroup>
              {touched.preferredPlatform && errors.preferredPlatform && <Typography sx={{ fontSize: '11px', color: '#ef4444', mt: 0.25 }}>{errors.preferredPlatform}</Typography>}
            </FormControl>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#e2e8f0' }} />

        {/* ── Alert Modules ── */}
        <Box>
          <SectionLabel text="Alert Modules" />
          <Box sx={{ maxWidth: 420 }}>
            <MultiSelectDropdown
              label="Interested Modules"
              options={availableModules.map(m => ({ code: m, name: m }))}
              selected={formData.modules}
              onToggle={(val, checked) => handleMultiSelectChange("modules", val, checked)}
              disabled={false}
              dropdownRef={modulesDropdownRef}
              showDropdown={showModulesDropdown}
              setShowDropdown={setShowModulesDropdown}
              onBlur={() => handleBlur("modules")}
              getLabel={(opt) => typeof opt === 'object' ? opt.name : opt}
              getValue={(opt) => typeof opt === 'object' ? opt.code : opt}
            />
          </Box>
          {formData.modules.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
              {formData.modules.map((mod) => (
                <Chip
                  key={mod}
                  label={mod}
                  size="small"
                  onDelete={() => handleMultiSelectChange("modules", mod, false)}
                  sx={{ fontSize: '12px', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', '& .MuiChip-deleteIcon': { color: '#93c5fd', '&:hover': { color: '#2563eb' } } }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* ── Submit ── */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={!isFormValid}
            sx={{
              background: '#2563eb',
              '&:hover': { background: '#1d4ed8' },
              '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8' },
              px: 4, py: 1, borderRadius: '8px', textTransform: 'none',
              fontSize: '14px', fontWeight: 600, boxShadow: 'none',
            }}
          >
            Submit
          </Button>
        </Box>

      </Box>
    </Paper>
  );
};

export default OfficialsinfoForm;
